import type { ArcadeReplayFrameSnapshot, ArcadeReplayPackage } from '../../arcade/contracts.js';

const FUMBLE_TRANSITION_FRAME_SCALE = 0.72;
const FUMBLE_RECOVERY_FOLLOW_THROUGH_FRAMES = 6;

function eventFrame(replay: ArcadeReplayPackage, type: string): number | null {
  const frame = Number(replay.events?.find((event: any) => event?.type === type)?.simFrame);
  return Number.isFinite(frame) ? frame : null;
}

function transitionBounds(replay: ArcadeReplayPackage): { start: number; end: number } | null {
  const forcedFrame = eventFrame(replay, 'fumble_forced');
  const recoveredFrame = eventFrame(replay, 'fumble_recovered');
  if (forcedFrame === null || recoveredFrame === null || recoveredFrame <= forcedFrame) return null;
  const start = replay.frames.findIndex((frame) => frame.simFrame >= forcedFrame);
  const recovery = replay.frames.findIndex((frame) => frame.simFrame >= recoveredFrame);
  if (start < 0 || recovery <= start) return null;
  return {
    start,
    end: Math.min(replay.frames.length - 1, recovery + FUMBLE_RECOVERY_FOLLOW_THROUGH_FRAMES),
  };
}

function compressedTransitionCount(length: number): number {
  return Math.max(3, Math.round(length * FUMBLE_TRANSITION_FRAME_SCALE));
}

export function fumblePresentedFrameCount(replay: ArcadeReplayPackage): number {
  const bounds = transitionBounds(replay);
  if (!bounds) return replay.frames.length;
  const originalLength = bounds.end - bounds.start + 1;
  return replay.frames.length - originalLength + compressedTransitionCount(originalLength);
}

export function presentFumbleTransitionFrames(
  replay: ArcadeReplayPackage,
): ArcadeReplayFrameSnapshot[] {
  const bounds = transitionBounds(replay);
  if (!bounds) return replay.frames;
  const source = replay.frames.slice(bounds.start, bounds.end + 1);
  const targetCount = compressedTransitionCount(source.length);
  const transition = Array.from({ length: targetCount }, (_, index) => {
    const sourceIndex = Math.round(index / Math.max(1, targetCount - 1) * (source.length - 1));
    return source[sourceIndex];
  });
  return [
    ...replay.frames.slice(0, bounds.start),
    ...transition,
    ...replay.frames.slice(bounds.end + 1),
  ];
}
